import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { getMisPermisos } from '../api';
import { calcularPermisosEfectivos } from '../config/permisos';
import { lightTheme, darkTheme } from '../theme';
import {
  Box, Drawer, Typography, IconButton,
  List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  Avatar, Divider, useMediaQuery, useTheme, Tooltip
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import ConstructionIcon from '@mui/icons-material/Construction';
import BuildIcon from '@mui/icons-material/Build';
import AssignmentIcon from '@mui/icons-material/Assignment';
import InventoryIcon from '@mui/icons-material/Inventory';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CalculateIcon from '@mui/icons-material/Calculate';
import LogoutIcon from '@mui/icons-material/Logout';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SchoolIcon from '@mui/icons-material/School';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import SuperHook from './SuperHook';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DialogoIncidencia from './DialogoIncidencia';

const DRAWER_WIDTH = 252;
const HEI_YELLOW = '#F5C200';
const HEI_YELLOW_DARK = '#C49B00';

const MENU = [
  { label: 'Inicio',         icon: <DashboardIcon fontSize="small" />,         path: '/',               permiso: null },

  { seccion: 'Operaciones' },
  { label: 'Máquinas',       icon: <ConstructionIcon fontSize="small" />,       path: '/maquinas',       permiso: 'ver_maquinas' },
  { label: 'Intervenciones', icon: <BuildIcon fontSize="small" />,              path: '/intervenciones', permiso: 'ver_intervenciones' },
  { label: 'Almacén',        icon: <InventoryIcon fontSize="small" />,          path: '/almacen',        permiso: 'ver_almacen' },
  { label: 'Tareas',         icon: <AssignmentIcon fontSize="small" />,         path: '/tareas',         permiso: 'ver_tareas' },

  { seccion: 'Comercial' },
  { label: 'Clientes',       icon: <PeopleIcon fontSize="small" />,             path: '/clientes',       permiso: 'ver_clientes' },
  { label: 'Comercial',      icon: <TrendingUpIcon fontSize="small" />,         path: '/comercial',      permiso: 'ver_comercial' },
  { label: 'Estudios',       icon: <CalculateIcon fontSize="small" />,          path: '/estudios',       permiso: 'estudios_operacion' },

  { seccion: 'Administración' },
  { label: 'Facturas',       icon: <ReceiptIcon fontSize="small" />,            path: '/facturas',       permiso: 'facturacion' },
  { label: 'Tesorería',      icon: <AccountBalanceIcon fontSize="small" />,     path: '/tesoreria',      permiso: 'tesoreria' },
  { label: 'Administración', icon: <ReceiptLongIcon fontSize="small" />,        path: '/administracion', permiso: 'ver_administracion' },
  { label: 'Config. facturación', icon: <SettingsIcon fontSize="small" />,      path: '/config-erp',     permiso: 'facturacion' },
  { label: 'Compras',        icon: <ShoppingCartIcon fontSize="small" />,       path: '/compras',        permiso: 'compras' },

  { seccion: 'RRHH' },
  { label: 'Usuarios',       icon: <AdminPanelSettingsIcon fontSize="small" />, path: '/usuarios',       permiso: 'ver_usuarios' },
  { label: 'HEI Academy',    icon: <SchoolIcon fontSize="small" />,             path: '/academy',        permiso: 'ver_academy' },
  { label: 'Incidencias',    icon: <HelpOutlineIcon fontSize="small" />,        path: '/incidencias',    permiso: 'gestionar_incidencias' },
];

function LayoutInner({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDark = theme.palette.mode === 'dark';
  const [mobileOpen, setMobileOpen] = useState(false);
  const [ayudaAbierta, setAyudaAbierta] = useState(false);
  const [permisosIndividuales, setPermisosIndividuales] = useState<string[]>([]);
  const [tieneEquipo, setTieneEquipo] = useState(false);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const esAdmin = user.rol === 'admin';
  const apiBase = process.env.REACT_APP_API_URL || '';

  // Colores del sidebar según modo
  const S = isDark ? {
    bg: '#141414',
    border: 'rgba(245,194,0,0.12)',
    text: 'rgba(255,255,255,0.65)',
    textActive: '#FFFFFF',
    hover: 'rgba(245,194,0,0.08)',
    active: 'rgba(245,194,0,0.15)',
    divider: 'rgba(255,255,255,0.07)',
    logoBg: '#1E1E1E',
    rolColor: HEI_YELLOW,
  } : {
    bg: '#FFFFFF',
    border: 'rgba(245,194,0,0.25)',
    text: '#5A5A5A',
    textActive: '#0D0D0D',
    hover: 'rgba(245,194,0,0.07)',
    active: 'rgba(245,194,0,0.15)',
    divider: 'rgba(0,0,0,0.07)',
    logoBg: '#F8F8F8',
    rolColor: HEI_YELLOW_DARK,
  };

  useEffect(() => {
    if (!esAdmin) {
      getMisPermisos().then(setPermisosIndividuales).catch(() => setPermisosIndividuales([]));
    }
    import('../api').then(({ tengoEquipo }) => {
      tengoEquipo().then(setTieneEquipo).catch(() => setTieneEquipo(false));
    });
  }, []);

  const permisosEfectivos = calcularPermisosEfectivos(user.rol, permisosIndividuales);
  const menuFiltrado = MENU.filter((item: any) => {
    if (item.seccion) return true;          // las cabeceras se filtran después
    if (!item.permiso) return true;
    // Caso especial: Usuarios visible también para responsables con equipo
    if (item.permiso === 'ver_usuarios') {
      return permisosEfectivos.includes('ver_usuarios') || tieneEquipo ||
             permisosEfectivos.includes('ver_todas_jornadas');
    }
    return permisosEfectivos.includes(item.permiso);
  });

  
  // Quita las cabeceras de sección que se quedan sin entradas visibles
  const menuFinal = menuFiltrado.filter((item: any, i: number) => {
    if (!item.seccion) return true;
    const siguiente = menuFiltrado[i + 1];
    return siguiente && !siguiente.seccion;
  });

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const drawer = (
    <Box sx={{
      height: '100%', display: 'flex', flexDirection: 'column',
      bgcolor: S.bg, overflowY: 'auto',
      borderRight: `1px solid ${S.border}`,
      pb: 'calc(env(safe-area-inset-bottom, 0px) + 24px)',
    }}>

      {/* Logo */}
      <Box sx={{ px: 2.5, pt: 3, pb: 2, textAlign: 'center' }}>
        <img
          src={isDark ? "/logo_hei_dark.png" : "/logo_hei.png"}
          alt="HEI"
          style={{ width: 150, display: 'block', margin: '0 auto' }}
        />
      </Box>

      <Divider sx={{ borderColor: S.divider }} />

      {/* Usuario */}
      <Box
        onClick={() => { navigate('/perfil'); if (isMobile) setMobileOpen(false); }}
        sx={{
          px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1.5,
          cursor: 'pointer', mx: 1, my: 0.5, borderRadius: 2,
          '&:hover': { bgcolor: S.hover }, transition: 'background 0.2s'
        }}
      >
        <Avatar
          src={user.foto ? `${apiBase}/perfil/foto/${user.id}` : ''}
          sx={{
            width: 40, height: 40, flexShrink: 0,
            border: `2px solid ${HEI_YELLOW}`,
            bgcolor: isDark ? '#2A2A2A' : '#EFEFEF',
            color: HEI_YELLOW, fontWeight: 700, fontSize: 16
          }}
        >
          {!user.foto && user.nombre?.[0]}
        </Avatar>
        <Box sx={{ overflow: 'hidden', flex: 1 }}>
          <Typography sx={{
            color: isDark ? '#FFFFFF' : '#0D0D0D',
            fontWeight: 600, fontSize: 13, lineHeight: 1.3,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
          }}>
            {user.nombre} {user.apellidos}
          </Typography>
          <Typography sx={{
            fontSize: 11, color: S.rolColor,
            fontWeight: 600, textTransform: 'capitalize'
          }}>
            {user.rol}
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: S.divider, mx: 2 }} />

      {/* Menú */}
      <List sx={{ flex: 1, px: 1, py: 1 }}>
        {menuFinal.map((item: any) => {
          if (item.seccion) {
            return (
              <Typography key={`sec-${item.seccion}`}
                sx={{ px: 2, pt: 2, pb: 0.5, fontSize: 10.5, fontWeight: 700,
                  letterSpacing: 0.8, textTransform: 'uppercase', color: S.text, opacity: 0.6 }}>
                {item.seccion}
              </Typography>
            );
          }
          const active = location.pathname === item.path ||
            (item.path !== '/' && location.pathname.startsWith(item.path));
          return (
            <ListItem key={item.path} disablePadding sx={{ mb: 0.25 }}>
              <ListItemButton
                onClick={() => { navigate(item.path); if (isMobile) setMobileOpen(false); }}
                sx={{
                  borderRadius: 1.5, py: 0.85, px: 1.5,
                  bgcolor: active ? S.active : 'transparent',
                  borderLeft: active ? `3px solid ${HEI_YELLOW}` : '3px solid transparent',
                  '&:hover': { bgcolor: active ? S.active : S.hover },
                  transition: 'all 0.15s',
                }}
              >
                <ListItemIcon sx={{
                  color: active ? HEI_YELLOW : S.text,
                  minWidth: 32, transition: 'color 0.15s'
                }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  sx={{
                    '& .MuiTypography-root': {
                      color: active ? S.textActive : S.text,
                      fontSize: 13.5,
                      fontWeight: active ? 600 : 400,
                      transition: 'all 0.15s'
                    }
                  }}
                />
                {active && (
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: HEI_YELLOW, flexShrink: 0 }} />
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Divider sx={{ borderColor: S.divider, mx: 2 }} />

      {/* Footer */}
      <List sx={{ px: 1, py: 0.5 }}>
        <ListItem disablePadding sx={{ mb: 0.25 }}>
          <ListItemButton
            onClick={() => setAyudaAbierta(true)}
            sx={{ borderRadius: 1.5, py: 0.85, px: 1.5, '&:hover': { bgcolor: S.hover } }}
          >
            <ListItemIcon sx={{ color: S.text, minWidth: 32 }}>
              <HelpOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Solicitar ayuda"
              sx={{ '& .MuiTypography-root': { color: S.text, fontSize: 13.5 } }}
            />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding sx={{ mb: 0.25 }}>
          <ListItemButton
            onClick={() => {
              const next = !isDark;
              localStorage.setItem('hei_theme', next ? 'dark' : 'light');
              window.location.reload();
            }}
            sx={{ borderRadius: 1.5, py: 0.85, px: 1.5, '&:hover': { bgcolor: S.hover } }}
          >
            <ListItemIcon sx={{ color: S.text, minWidth: 32 }}>
              {isDark ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText
              primary={isDark ? 'Modo claro' : 'Modo oscuro'}
              sx={{ '& .MuiTypography-root': { color: S.text, fontSize: 13.5 } }}
            />
          </ListItemButton>
        </ListItem>

        <ListItem disablePadding>
          <ListItemButton
            onClick={handleLogout}
            sx={{ borderRadius: 1.5, py: 0.85, px: 1.5, '&:hover': { bgcolor: 'rgba(220,38,38,0.10)' } }}
          >
            <ListItemIcon sx={{ color: '#EF4444', minWidth: 32 }}>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Cerrar sesión"
              sx={{ '& .MuiTypography-root': { color: '#EF4444', fontSize: 13.5, fontWeight: 500 } }}
            />
          </ListItemButton>
        </ListItem>
      </List>

      <Box sx={{ px: 2, pb: 1.5, textAlign: 'center' }}>
        <Typography sx={{ fontSize: 10, color: isDark ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.2)' }}>
          HEI SIS · Plataforma de gestión
        </Typography>
      </Box>
      <DialogoIncidencia open={ayudaAbierta} onClose={() => setAyudaAbierta(false)} />
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Botón flotante para abrir el menú (solo móvil) */}
      {!mobileOpen && (
        <IconButton
          onClick={() => setMobileOpen(true)}
          sx={{
            display: { md: 'none' },
            position: 'fixed', top: 10, left: 10, zIndex: theme.zIndex.drawer + 1,
            bgcolor: isDark ? 'rgba(30,30,30,0.92)' : 'rgba(255,255,255,0.94)',
            border: `1px solid ${HEI_YELLOW}`,
            boxShadow: '0 2px 10px rgba(0,0,0,0.25)',
            '&:hover': { bgcolor: isDark ? '#2A2A2A' : '#F5F5F5' },
          }}
        >
          <MenuIcon sx={{ color: HEI_YELLOW }} />
        </IconButton>
      )}

      {/* Sidebar */}
      <Drawer
        variant={isMobile ? 'temporary' : 'permanent'}
        open={isMobile ? mobileOpen : true}
        onClose={() => setMobileOpen(false)}
        sx={{
          width: DRAWER_WIDTH, flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH, boxSizing: 'border-box',
            border: 'none', height: '100dvh', overflowY: 'auto',
            boxShadow: isDark ? '4px 0 24px rgba(0,0,0,0.5)' : '4px 0 24px rgba(0,0,0,0.06)',
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Contenido */}
      <Box component="main" sx={{
        flexGrow: 1, p: { xs: 1.5, sm: 2, md: 3 },
        pt: { xs: 7, sm: 7, md: 3 }, minHeight: '100vh',
        bgcolor: 'background.default',
        width: '100%', overflowX: 'hidden', boxSizing: 'border-box',
      }}>
        {children}
      </Box>

      {/* Asistente IA flotante (en todas las páginas) */}
      <SuperHook />
    </Box>
  );
}

export default function Layout({ children }: { children: React.ReactNode }) {
  const isDark = localStorage.getItem('hei_theme') === 'dark';
  return (
    <ThemeProvider theme={isDark ? darkTheme : lightTheme}>
      <CssBaseline />
      <LayoutInner>{children}</LayoutInner>
    </ThemeProvider>
  );
}